import { useMemo, useRef, useCallback } from 'react';
import { Coordinates, RouteFollowState, RouteFollowPoi } from '../types';
import { getDistance } from '../services/geometryUtils';

const OFF_ROUTE_THRESHOLD_M = 200;
const NEARBY_POI_THRESHOLD_M = 150;
const PASSED_POI_THRESHOLD_M = 80;

export interface RouteFollowResult {
  isFollowing: boolean;
  progressM: number | null;
  progressPercent: number;
  distanceFromRoute: number | null;
  nearestRoutePoint: Coordinates | null;
  nearestRouteIndex: number | null;
  nearestRouteDistanceM: number | null;
  nearestPointDistanceAlongM: number | null;
  isOffRoute: boolean;
  nextPois: RouteFollowPoi[];
  passedPois: RouteFollowPoi[];
  nearbyPoi: RouteFollowPoi | null;
}

const EMPTY_RESULT: RouteFollowResult = {
  isFollowing: false,
  progressM: null,
  progressPercent: 0,
  distanceFromRoute: null,
  nearestRoutePoint: null,
  nearestRouteIndex: null,
  nearestRouteDistanceM: null,
  nearestPointDistanceAlongM: null,
  isOffRoute: false,
  nextPois: [],
  passedPois: [],
  nearbyPoi: null,
};

/**
 * Pre-beregner kumulativ distanse langs rutekoordinater (i meter).
 * Returnerer array med samme lengde som coords, der [i] = kumulativ distanse til punkt i.
 */
function buildCumulativeDistances(coords: Coordinates[]): number[] {
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const d = getDistance(
      coords[i - 1].latitude, coords[i - 1].longitude,
      coords[i].latitude, coords[i].longitude
    );
    cum.push(cum[i - 1] + d);
  }
  return cum;
}

/**
 * Finner nærmeste segment på ruten og returnerer projisert distanse langs ruten.
 * Returnerer { distanceAlongM, distanceFromRouteM }.
 */
function projectOntoRoute(
  userLat: number,
  userLon: number,
  coords: Coordinates[],
  cumDist: number[]
): {
  distanceAlongM: number;
  distanceFromRouteM: number;
  nearestPoint: Coordinates;
  nearestIndex: number;
} {
  let minDist = Infinity;
  let bestAlongM = 0;
  let bestPoint: Coordinates = coords[0];
  let bestIndex = 0;

  for (let i = 0; i < coords.length - 1; i++) {
    const v = coords[i];
    const w = coords[i + 1];

    // Projiser brukerens posisjon på segmentet v→w
    const vLat = v.latitude, vLon = v.longitude;
    const wLat = w.latitude, wLon = w.longitude;

    const dx = wLat - vLat;
    const dy = wLon - vLon;
    const segLenSq = dx * dx + dy * dy;

    let t = 0;
    if (segLenSq > 0) {
      t = ((userLat - vLat) * dx + (userLon - vLon) * dy) / segLenSq;
      t = Math.max(0, Math.min(1, t));
    }

    const projLat = vLat + t * dx;
    const projLon = vLon + t * dy;
    const dist = getDistance(userLat, userLon, projLat, projLon);

    if (dist < minDist) {
      minDist = dist;
      // Kumulativ distanse til projisert punkt på segmentet
      const segLen = cumDist[i + 1] - cumDist[i];
      bestAlongM = cumDist[i] + t * segLen;
      bestPoint = { latitude: projLat, longitude: projLon };
      bestIndex = i;
    }
  }

  return {
    distanceAlongM: bestAlongM,
    distanceFromRouteM: minDist,
    nearestPoint: bestPoint,
    nearestIndex: bestIndex,
  };
}

export function useRouteFollow(
  followState: RouteFollowState | null,
  userPosition: Coordinates | null,
  _userHeading: number | null
): RouteFollowResult {
  // Ref for passerte POI-er (monoton — legges til, fjernes aldri under turen)
  const passedPoiIdsRef = useRef<Set<string>>(new Set());
  const nearestPointRef = useRef<{ point: Coordinates; index: number } | null>(null);

  // Nullstill passerte POI-er når followState endres (ny tur)
  const prevRouteIdRef = useRef<string | null>(null);
  if (followState?.routeId !== prevRouteIdRef.current) {
    prevRouteIdRef.current = followState?.routeId ?? null;
    passedPoiIdsRef.current = new Set();
    nearestPointRef.current = null;
  }

  // Pre-beregn kumulative distanser (stabil referanse så lenge routeCoords er lik)
  const cumDist = useMemo(() => {
    if (!followState) return [];
    return buildCumulativeDistances(followState.routeCoords);
  }, [followState?.routeCoords]);

  // Callback for å markere en POI som passert
  const markPassed = useCallback((id: string) => {
    passedPoiIdsRef.current.add(id);
  }, []);

  return useMemo<RouteFollowResult>(() => {
    if (!followState || !userPosition || cumDist.length === 0) {
      return EMPTY_RESULT;
    }

    const { routeCoords, routeTotalDistanceM, pois } = followState;
    if (routeCoords.length < 2) return EMPTY_RESULT;

    // Projiser brukerens posisjon på ruten
    const {
      distanceAlongM,
      distanceFromRouteM,
      nearestPoint,
      nearestIndex,
    } = projectOntoRoute(
      userPosition.latitude,
      userPosition.longitude,
      routeCoords,
      cumDist
    );
    let stableNearestPoint = nearestPoint;
    let stableNearestIndex = nearestIndex;
    if (nearestPointRef.current) {
      const jumpDist = getDistance(
        nearestPointRef.current.point.latitude,
        nearestPointRef.current.point.longitude,
        nearestPoint.latitude,
        nearestPoint.longitude
      );
      if (jumpDist < 20) {
        stableNearestPoint = nearestPointRef.current.point;
        stableNearestIndex = nearestPointRef.current.index;
      } else {
        nearestPointRef.current = { point: nearestPoint, index: nearestIndex };
      }
    } else {
      nearestPointRef.current = { point: nearestPoint, index: nearestIndex };
    }

    const isOffRoute = distanceFromRouteM > OFF_ROUTE_THRESHOLD_M;
    const progressPercent = routeTotalDistanceM > 0
      ? Math.min(100, (distanceAlongM / routeTotalDistanceM) * 100)
      : 0;

    // Oppdater passerte POI-er (monoton)
    for (const poi of pois) {
      if (poi.distanceAlongRoute < distanceAlongM - PASSED_POI_THRESHOLD_M) {
        markPassed(poi.id);
      }
    }

    const passedPois = pois.filter(p => passedPoiIdsRef.current.has(p.id));
    const upcomingPois = pois.filter(p => !passedPoiIdsRef.current.has(p.id) && p.distanceAlongRoute > distanceAlongM);
    const nextPois = upcomingPois.slice(0, 3);

    // Sjekk om det er en POI i nærheten (absolutt avstand, ikke langs ruten)
    let nearbyPoi: RouteFollowPoi | null = null;
    for (const poi of upcomingPois) {
      const d = getDistance(
        userPosition.latitude, userPosition.longitude,
        poi.location.latitude, poi.location.longitude
      );
      if (d < NEARBY_POI_THRESHOLD_M) {
        nearbyPoi = poi;
        break;
      }
    }

    return {
      isFollowing: true,
      progressM: distanceAlongM,
      progressPercent,
      distanceFromRoute: distanceFromRouteM,
      nearestRoutePoint: stableNearestPoint,
      nearestRouteIndex: stableNearestIndex,
      nearestRouteDistanceM: distanceFromRouteM,
      nearestPointDistanceAlongM: distanceAlongM,
      isOffRoute,
      nextPois,
      passedPois,
      nearbyPoi,
    };
  }, [followState, userPosition, cumDist, markPassed]);
}
